import type { GameBoardOverlayInterface } from "@/game/context/GameBoardOverlayContext.ts";
import { createDiceRollSlideShow } from "@/game/utils/diceDisplayUtils.ts";
import { GameLogger } from "@/game/utils/gameLogger.ts";
import type { PlayerColor } from "@nextgammon/shared";
import { Constants, getGameMode } from "@nextgammon/shared";
import { useEffect, useMemo, useRef } from "react";

import { Easing, Group, Tween } from "@tweenjs/tween.js";
import * as PIXI from "pixi.js";
import { Texture } from "pixi.js";

interface InitialDiceRollProps {
  initialDice: number[] | null;
  playerTextures: Texture[];
  opponentTextures: Texture[];
  playerColor: PlayerColor;
  currentPlayerColor: PlayerColor;
  playerUsername?: string;
  opponentUsername?: string;
  diceArea: [number, number, number, number];
  playArea: [number, number, number, number];
  diceScale: number;
  boardScale: number;
  shouldShowRoll: boolean;
  overlayInterface: GameBoardOverlayInterface | null;
  onRollAnimationComplete: () => void;
  gameMode: string; // NEW: Game mode for animation timing
}

export function useDiceRollInitial({
  initialDice,
  playerTextures,
  opponentTextures,
  playerColor,
  currentPlayerColor,
  playerUsername,
  opponentUsername,
  diceArea,
  playArea,
  diceScale,
  boardScale,
  shouldShowRoll,
  overlayInterface,
  onRollAnimationComplete,
  gameMode
}: InitialDiceRollProps) {
  const inProgressRef = useRef<boolean>(false);
  const completedRef = useRef<boolean>(false);
  // Load the background for dice rolls
  const rollBG = useMemo(() => PIXI.Assets.get("dice-initial-roll-bg"), []);
  const rollWinnerHighlight = useMemo(() => PIXI.Assets.get("dice-initial-roll-highlight"), []);

  // Calculate animation duration based on game mode
  const mode = getGameMode(gameMode);
  const ROLL_ANIMATION_TIME_NORMAL = Math.round(4000 * mode.animation.diceRollDurationMultiplier);

  const keyBG = `initial-dice-roll-bg`;
  const keyBGHighLight = `initial-dice-roll-bg-highlight`;
  const keyLabel = `initial-dice-roll-label`;
  const keySlides = `initial-dice-roll-slides-`;
  const keyBlend = `initial-dice-roll-blend`;

  const cleanupRollAnimation = () => {
    if (overlayInterface) {
      overlayInterface.removeSprite(keyBG);
      overlayInterface.removeSprite(keyBGHighLight);
      overlayInterface.removeSprite(keyLabel);
      overlayInterface.removeSprite(keyBlend);
      for (let i = 1; i <= 2; i++) {
        overlayInterface.removeSprite(keySlides + i, 100);
      }
    }
  };

  // --- Roll Animation Helpers ---

  const setupRollAnimationBackground = (
    texture: Texture,
    playAreaCenter: { x: number; y: number },
    startDelay: number,
    totalRollAnimTime: number,
    rotation: number,
    tweenKey: string
  ) => {
    const spriteBG = new PIXI.Sprite(texture);
    spriteBG.anchor.set(0.5, 0.5);
    spriteBG.position.set(playAreaCenter.x, playAreaCenter.y);
    spriteBG.rotation = rotation;
    spriteBG.scale.set(boardScale, boardScale);
    spriteBG.alpha = 0;
    spriteBG.roundPixels = true;

    const groupBG = new Group();
    const alphaTweenInBG = new Tween({ alpha: 0 })
      .to({ alpha: 1 }, 500)
      .delay(startDelay)
      .onUpdate((values: { alpha: number }) => {
        spriteBG.alpha = values.alpha;
      });
    groupBG.add(alphaTweenInBG);

    const alphaTweenOutBG = new Tween({ alpha: 1 })
      .to({ alpha: 0 }, 500)
      .delay(totalRollAnimTime - 500)
      .onUpdate((values: { alpha: number }) => {
        spriteBG.alpha = values.alpha;
      });
    groupBG.add(alphaTweenOutBG);

    overlayInterface?.addSpriteWithTween(tweenKey, spriteBG, groupBG, true);
  };

  const setupRollAnimationSlideshow = (
    textures: Texture[],
    diceNumber: number,
    scale: number,
    fadeOut: boolean,
    playAreaCenter: { x: number; y: number },
    destination: { x: number; y: number },
    totalRollAnimTime: number
  ): { sprite: PIXI.AnimatedSprite; group: Group; duration: number } => {
    const { sprite, group, duration } = createDiceRollSlideShow(
      textures,
      diceNumber - 1,
      totalRollAnimTime,
      0,
      scale,
      { x: playArea[0], y: playArea[1] },
      playAreaCenter,
      diceScale,
      { x: destination.x, y: destination.y },
      undefined
    );

    if (fadeOut) {
      const alphaTweenOut = new Tween({ alpha: 1 })
        .to({ alpha: 0 }, 500)
        .delay(totalRollAnimTime - 500)
        .onUpdate((values: { alpha: number }) => {
          sprite.alpha = values.alpha;
        });
      group.add(alphaTweenOut);
    }
    return { sprite, group, duration };
  };

  const setupRollAnimationBlending = (
    texture: Texture,
    scale: number,
    playAreaCenter: { x: number; y: number },
    startDelay: number,
    destination: { x: number; y: number },
    totalRollAnimTime: number
  ): { sprite: PIXI.Sprite; group: Group } => {
    const duration = totalRollAnimTime - startDelay;

    const sprite = new PIXI.Sprite(texture);
    sprite.anchor.set(0.5, 0.5);
    sprite.position.set(playAreaCenter.x, playAreaCenter.y);
    sprite.scale.set(scale, scale);
    sprite.roundPixels = true;
    sprite.alpha = 0;

    const group = new Group();
    const alphaTween = new Tween({ alpha: 0 })
      .to({ alpha: 1 }, 500)
      .delay(startDelay)
      .onUpdate((values: { alpha: number }) => {
        sprite.alpha = values.alpha;
      });
    group.add(alphaTween);

    const moveStartX = playAreaCenter.x - playArea[0];
    const moveStartY = playAreaCenter.y - playArea[1];

    const moveTween = new Tween({ value: 0 })
      .to({ value: 1 }, duration)
      .easing(Easing.Quadratic.InOut)
      .delay(startDelay)
      .onUpdate((current) => {
        sprite.position.set(
          moveStartX + (destination.x - playArea[0] - moveStartX) * current.value,
          moveStartY + (destination.y - playArea[1] - moveStartY) * current.value
        );
      });
    group.add(moveTween);

    const scaleTween = new Tween({ scaleX: scale, scaleY: scale })
      .to({ scaleX: diceScale, scaleY: diceScale }, duration)
      .delay(startDelay)
      .onUpdate((values: { scaleX: number; scaleY: number }) => {
        sprite.scale.set(values.scaleX, values.scaleY);
      });
    group.add(scaleTween);

    return { sprite, group };
  };

  const setupRollAnimationLabels = (
    playAreaCenter: { x: number; y: number },
    rollDuration: number,
    totalRollAnimTime: number,
    playerColor: PlayerColor,
    currentPlayerColor: PlayerColor,
    playerUsername?: string,
    opponentUsername?: string
  ) => {
    const labelStyle = new PIXI.TextStyle({
      fontFamily: "Arial",
      fontSize: 96 * boardScale,
      fill: "#ffd960",
      fontWeight: "bold",
      stroke: 0x000000,
      strokeThickness: 2,
      align: "center"
    });
    // The current player is the winner of the initial roll
    const labelText =
      currentPlayerColor === playerColor
        ? "You start!"
        : `${opponentUsername || "Opponent"} starts!`;
    const winnerLabel = new PIXI.Text(labelText, labelStyle);
    winnerLabel.anchor.set(0.5, 0.5);
    winnerLabel.position.set(playAreaCenter.x, playAreaCenter.y);
    winnerLabel.alpha = 0;

    const groupLabel1 = new Group();
    const alphaTweenInLabel1 = new Tween({ alpha: 0 })
      .to({ alpha: 1 }, 200)
      .delay(rollDuration)
      .onUpdate((values: { alpha: number }) => {
        winnerLabel.alpha = values.alpha;
      });
    groupLabel1.add(alphaTweenInLabel1);

    const alphaTweenOutLabel1 = new Tween({ alpha: 1 })
      .to({ alpha: 0 }, 500)
      .delay(totalRollAnimTime - 500)
      .onUpdate((values: { alpha: number }) => {
        winnerLabel.alpha = values.alpha;
      });
    groupLabel1.add(alphaTweenOutLabel1);

    overlayInterface?.addSpriteWithTween(keyLabel, winnerLabel, groupLabel1, true);
  };

  const setAnimationCompleted = (triggerCallback: boolean) => {
    cleanupRollAnimation();
    inProgressRef.current = false;
    completedRef.current = true;

    GameLogger.logInfo(
      "useDiceRollInitial",
      "Set roll animation completed triggerCallback: " + triggerCallback
    );

    if (triggerCallback) {
      onRollAnimationComplete();
    }
  };

  // Handle the roll interstitial animation
  useEffect(() => {
    GameLogger.logDebug(
      "useDiceRollInitial",
      `useEffect: shouldShowRoll: ${shouldShowRoll}, overlayInterface: ${overlayInterface && overlayInterface.isPlayAreaValid() ? "available" : "not available"}`
    );

    // Can't show the initial roll without the required resources
    if (
      !overlayInterface ||
      !overlayInterface.isPlayAreaValid() ||
      !rollBG ||
      !rollWinnerHighlight ||
      !playerTextures ||
      !opponentTextures
    ) {
      return;
    }

    if (inProgressRef.current) {
      GameLogger.logInfo(
        "useDiceRollInitial",
        `Roll animation in progress, shouldShowRoll: ${shouldShowRoll}`
      );

      if (shouldShowRoll) {
        return;
      }
    }

    // Bail if not showing the initial roll interstitial
    if (!shouldShowRoll) {
      setAnimationCompleted(false);
      return;
    } else {
      if (!initialDice || initialDice.length < 2) {
        GameLogger.logWarn("useDiceRollInitial", "No dice to roll, skipping roll animation");
        setAnimationCompleted(false);
        return;
      }
    }

    if (shouldShowRoll) {
      GameLogger.logInfo("useDiceRollInitial", "Starting roll animation");
      inProgressRef.current = true;
      completedRef.current = false;

      const whiteDie = initialDice[0];
      const blackDie = initialDice[1];
      // Determine which die belongs to which player based on player color
      const playerDie = playerColor === Constants.PLAYER_WHITE ? whiteDie : blackDie;
      const opponentDie = playerColor === Constants.PLAYER_WHITE ? blackDie : whiteDie;

      // The winner is simply whoever's turn it is after the initial roll (currentPlayerColor)
      const winnerColor = currentPlayerColor;

      GameLogger.logInfo(
        "useDiceRollInitial",
        `Winner color: ${winnerColor}, whiteDie: ${whiteDie}, blackDie: ${blackDie}, playerColor: ${playerColor}, currentPlayerColor: ${currentPlayerColor}`
      );

      const playAreaCenter = { x: playArea[0] + playArea[2] / 2, y: playArea[1] + playArea[3] / 2 };
      const diceWidth = playerTextures[0].width * diceScale;
      const diceHeight = playerTextures[0].height * diceScale;
      const spriteScale = boardScale * 1.25;
      const offsetX = rollBG.width * boardScale * 0.25;
      const offsetY = rollBG.height * boardScale * 0.25;
      const endX = diceArea[0] + diceArea[2] / 2;
      const endY = diceArea[1] + diceHeight / 2;

      const playerEnd =
        playerColor === Constants.PLAYER_WHITE
          ? { x: endX, y: endY }
          : { x: endX, y: endY + diceArea[3] / 4 };
      const opponentEnd =
        playerColor === Constants.PLAYER_WHITE
          ? { x: endX, y: endY + diceArea[3] / 4 }
          : { x: endX, y: endY };

      let rollDuration = 0;
      let playerRollAnimation;
      let opponentRollAnimation;
      let blendRollAnimation;

      // Show player's die with player textures
      if (playerDie) {
        playerRollAnimation = setupRollAnimationSlideshow(
          playerTextures,
          playerDie,
          spriteScale,
          winnerColor !== playerColor,
          { x: playAreaCenter.x - offsetX, y: playAreaCenter.y - offsetY },
          playerEnd,
          ROLL_ANIMATION_TIME_NORMAL
        );
        rollDuration = playerRollAnimation.duration;

        if (winnerColor !== playerColor) {
          blendRollAnimation = setupRollAnimationBlending(
            opponentTextures[playerDie - 1],
            spriteScale,
            { x: playAreaCenter.x - offsetX, y: playAreaCenter.y - offsetY },
            rollDuration,
            playerEnd,
            ROLL_ANIMATION_TIME_NORMAL
          );
        }
      }

      // Show opponent's die with opponent textures
      if (opponentDie) {
        opponentRollAnimation = setupRollAnimationSlideshow(
          opponentTextures,
          opponentDie,
          spriteScale,
          winnerColor === playerColor,
          { x: playAreaCenter.x + offsetX, y: playAreaCenter.y + offsetY },
          opponentEnd,
          ROLL_ANIMATION_TIME_NORMAL
        );
        rollDuration = Math.max(rollDuration, opponentRollAnimation.duration);

        if (winnerColor === playerColor) {
          blendRollAnimation = setupRollAnimationBlending(
            playerTextures[opponentDie - 1],
            spriteScale,
            { x: playAreaCenter.x + offsetX, y: playAreaCenter.y + offsetY },
            rollDuration,
            opponentEnd,
            ROLL_ANIMATION_TIME_NORMAL
          );
        }
      }

      setupRollAnimationBackground(rollBG, playAreaCenter, 0, ROLL_ANIMATION_TIME_NORMAL, 0, keyBG);
      setupRollAnimationBackground(
        rollWinnerHighlight,
        playAreaCenter,
        rollDuration,
        ROLL_ANIMATION_TIME_NORMAL,
        currentPlayerColor === Constants.PLAYER_WHITE ? 0 : Math.PI,
        keyBGHighLight
      );

      if (playerRollAnimation) {
        overlayInterface.addSpriteWithTween(
          keySlides + 1,
          playerRollAnimation.sprite,
          playerRollAnimation.group,
          true
        );
      }
      if (opponentRollAnimation) {
        overlayInterface.addSpriteWithTween(
          keySlides + 2,
          opponentRollAnimation.sprite,
          opponentRollAnimation.group,
          true
        );
      }
      if (blendRollAnimation) {
        overlayInterface.addSpriteWithTween(
          keyBlend,
          blendRollAnimation.sprite,
          blendRollAnimation.group,
          true
        );
      }

      setupRollAnimationLabels(
        playAreaCenter,
        rollDuration,
        ROLL_ANIMATION_TIME_NORMAL,
        playerColor,
        currentPlayerColor,
        playerUsername,
        opponentUsername
      );

      const timerId = setTimeout(() => {
        GameLogger.logInfo("useDiceRollInitial", "Roll animation complete");

        setAnimationCompleted(true);
      }, ROLL_ANIMATION_TIME_NORMAL);

      return () => {
        GameLogger.logInfo("useDiceRollInitial", "Cleaning up roll animation on unmount");
        clearTimeout(timerId);

        setAnimationCompleted(false);
      };
    }
  }, [shouldShowRoll, overlayInterface, initialDice, playArea]);

  return {
    isInitialRollInProgress: inProgressRef.current
  };
}
